import React, { useState, useRef, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";

import Cipher from "services/cipher";
import CustomerEmail from "components/CustomerEmail";
import GlobalSearch from "components/globalSearch";

import { BASE_ASSETS_URL } from "constants/app";
import { API_FETCH_STATUS, LIB_CONSUMER, LOB } from "constants/common";

import { isMobile } from "utils/deviceType";
import { trackAnalytics } from "services/analytics";
import { trackMOEEvent } from "utils/analytics";

import Environment from "services/environment";

import { generateUrl } from "utils/generateUrl";
import { isGuestUser } from "utils/guestUtils";

import { LINK_TO_ROUTE } from "constants/common";

import Help from "./help/help";
import Profile from "./profile/profile";
import Notification from "./notification/notification";

import IconLoader from "./loaders/IconLoader/loader";

import { MOE_HEADER } from "./constants";

import styles from "./postLoginHeader.scss";
import OutsideAlerter from "components/outsideAlerter";
import ProfileCard from "components/postLoginHeader/profileCard/profile-card.component";

const env = Environment.getEnv();
const { LOADING, SUCCESS } = API_FETCH_STATUS;

const AirtelLogo = ({ source }) => {
  const guestUser = isGuestUser();
  return source === LIB_CONSUMER.ATB ? (
    <Link
      to={generateUrl(LINK_TO_ROUTE.DASHBOARD, guestUser ? { lob: LOB.GUEST_DASHBOARD } : {}, {})}
      className={styles.main_logo}
    >
      <img
        className={styles.display_big}
        src={`${BASE_ASSETS_URL}images/airtel-business-logo.svg`}
        alt="Airtel Business"
        width="84px"
        height="34px"
      />
      <img className={styles.display_small} src={`${BASE_ASSETS_URL}images/airtel.svg`} alt="Airtel Business" />
    </Link>
  ) : (
    <a href={env.selfcareBasePageUrl} className={styles.main_logo}>
      <img
        className={styles.display_big}
        src={`${BASE_ASSETS_URL}images/airtel-business-logo.svg`}
        alt="Airtel Business"
        width="84px"
        height="34px"
      />
      <img className={styles.display_small} src={`${BASE_ASSETS_URL}images/airtel.svg`} alt="Airtel Business" />
    </a>
  );
};

const UserLogo = ({ config, clientLogoUrl }) => {
  if (config.state === LOADING) {
    return <IconLoader />;
  }

  if (config.state === SUCCESS) {
    const { headerConfig } = config.contents;

    const userLogo = useMemo(
      () => headerConfig.logoUploadAllowed && (clientLogoUrl ?? headerConfig.logo),
      [headerConfig.logoUploadAllowed, clientLogoUrl, headerConfig.logo],
    );

    return headerConfig.logo || clientLogoUrl ? (
      <span className={`${styles.sub_logo}`}>
        <img src={userLogo} alt="user Logo" />
      </span>
    ) : null;
  }

  return null;
};

const ProfileIcon = ({
  source,
  profile,
  onLogout,
  activeDropdown,
  setActiveDropdown,
  profileCompleteness,
  lobDetails,
  partyDetails,
  showProfileCompleteness,
  profilePictureUrl,
}) => {
  if (profile.state === LOADING) {
    return <IconLoader />;
  }

  if (profile.state === SUCCESS) {
    return (
      <ProfileCard
        {...{
          source,
          profile: profile.contents,
          onLogout,
          activeDropdown,
          setActiveDropdown,
          profileCompleteness,
          lobDetails,
          partyDetails,
          showProfileCompleteness,
          profilePictureUrl,
        }}
      />
    );
  }

  return null;
};

const BellIcon = ({ source, activeDropdown, setActiveDropdown, notifications, markNotificationRead, lob, partyId }) => {
  if (notifications.state === LOADING) {
    return <IconLoader />;
  }

  if (notifications.state === SUCCESS) {
    return (
      <Notification
        {...{
          source,
          activeDropdown,
          setActiveDropdown,
          notifications: notifications.contents.data,
          markNotificationRead,
          lob,
          partyId,
        }}
      />
    );
  }

  return null;
};

const SecondaryUserEmail = ({ profile }) => {
  if (profile.state === LOADING) {
    return <IconLoader />;
  }

  if (profile.state === SUCCESS && profile.contents.isReadOnlySuperAdminLogin && env.ENABLE_READ_ONLY_SUPER_ADMIN_JOURNEY) {
    return <CustomerEmail value={getCustomerEmail()} label="Customer Email" onClick={navigateToSelectCustomer} />;
  }

  return null;
};

const navigateToSelectCustomer = () => (window.location.href = env.selfcareSelectCustomerAccountPageUrl);

const getCustomerEmail = () => {
  const sIvUser = localStorage.getItem("s-iv-user");
  if (sIvUser) {
    const cipher = new Cipher();
    const decryptSIvuser = cipher.decrypt(sIvUser);
    return decryptSIvuser["s-iv-user"];
  }
  return "";
};

export default function PostLoginHeader({
  source,
  config,
  profile,
  onLogout,
  notifications,
  markNotificationRead,
  profileCompleteness,
  lobDetails,
  partyDetails,
  showProfileCompleteness = true,
  showNotificationIcon = true,
  clientLogoUrl,
  profilePictureUrl,
  showGlobalSearchTour,
  onGlobalSearchTourDismiss,
  showProductTour,
  lob,
  product,
  showNumericSearchTour,
  onNumericSearchTourDismiss,
}) {
  const history = useHistory();
  const headerRef = useRef();
  const [activeDropdown, setActiveDropdown] = useState(null);

  const faOnDropdownClick = (name) => {
    trackAnalytics({
      eVar9: `b2b|aeh|${name?.toLowerCase()}`,
    });
  };

  const handleDropdownSelect = (dropdown) => {
    if (dropdown === activeDropdown) setActiveDropdown(null);
    else {
      trackMOEEvent(`${MOE_HEADER}_${dropdown}_CLICK`);
      faOnDropdownClick(dropdown);
      setActiveDropdown(dropdown);
    }
  };

  return (
    <div className={styles.headerSection} ref={headerRef}>
      <header className={styles.authHeader}>
        <div className="container">
          <div className={styles.inside_header}>
            <div className={styles.logo}>
              <AirtelLogo
                source={source}
                history={history}
                profile={profile}
                lobDetails={lobDetails}
                partyDetails={partyDetails}
              />
              <UserLogo config={config} clientLogoUrl={clientLogoUrl} />
            </div>
            <GlobalSearch
              {...{
                showGlobalSearchTour,
                onGlobalSearchTourDismiss,
                showNumericSearchTour,
                onNumericSearchTourDismiss,
                lob,
                partyDetails,
                profile,
                product,
              }}
            />
            <div className={styles.navbar_right}>
              <OutsideAlerter onOutsideClick={() => setActiveDropdown(null)}>
                <ul className={styles.navlist}>
                  {!isMobile() && <SecondaryUserEmail {...{ profile }} />}
                  <Help
                    {...{
                      source,
                      activeDropdown,
                      setActiveDropdown: handleDropdownSelect,
                      isInternalUser: profile.contents?.isInternalUser,
                      showProductTour,
                    }}
                  />
                  {showNotificationIcon && (
                    <BellIcon
                      {...{
                        source,
                        activeDropdown,
                        notifications,
                        markNotificationRead,
                        setActiveDropdown: handleDropdownSelect,
                        lob: lobDetails?.key,
                        partyId: partyDetails?.partyId,
                      }}
                    />
                  )}
                  <ProfileIcon
                    {...{
                      source,
                      profile,
                      onLogout,
                      activeDropdown,
                      setActiveDropdown: handleDropdownSelect,
                      profileCompleteness,
                      lobDetails,
                      partyDetails,
                      showProfileCompleteness,
                      profilePictureUrl,
                    }}
                  />
                </ul>
              </OutsideAlerter>
            </div>
          </div>
          {isMobile() && (
            <div className={styles.other_links}>
              <div className={styles.right_wrapper}>
                <SecondaryUserEmail {...{ profile }} />
              </div>
            </div>
          )}
        </div>
      </header>
    </div>
  );
}
